import { useState, useRef, useEffect, useCallback } from 'react'
import WaveSurfer from 'wavesurfer.js'
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js'

interface AudioRecorderProps {
  /** Called when a recording is ready (stopped). Parent decides when to send. */
  onReady: (file: File) => void
  onCancel: () => void
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

export function AudioRecorder({ onReady, onCancel }: AudioRecorderProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const wavesurferRef = useRef<WaveSurfer | null>(null)
  const recordRef = useRef<RecordPlugin | null>(null)

  const [isRecording, setIsRecording] = useState(true)
  const [hasRecording, setHasRecording] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState('00:00')

  useEffect(() => {
    if (!containerRef.current) return

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#1F93FF',
      progressColor: '#6E6F73',
      height: 48,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      cursorWidth: 0,
    })

    const record = ws.registerPlugin(
      RecordPlugin.create({
        scrollingWaveform: true,
        renderRecordedAudio: false,
      })
    )

    wavesurferRef.current = ws
    recordRef.current = record

    ws.on('finish', () => setIsPlaying(false))
    ws.on('play', () => setIsPlaying(true))
    ws.on('pause', () => setIsPlaying(false))

    record.on('record-progress', (time: number) => {
      setDuration(formatDuration(time))
    })

    record.on('record-end', async (blob: Blob) => {
      const audioUrl = URL.createObjectURL(blob)
      const mimeType = blob.type || 'audio/webm'
      let ext = 'webm'
      if (mimeType.includes('ogg')) ext = 'ogg'
      else if (mimeType.includes('mp4') || mimeType.includes('m4a')) ext = 'mp4'
      else if (mimeType.includes('mp3') || mimeType.includes('mpeg')) ext = 'mp3'

      const file = new File([blob], `audio-${Date.now()}.${ext}`, { type: mimeType })

      setHasRecording(true)
      setIsRecording(false)
      ws.load(audioUrl)
      onReady(file)
    })

    record.startRecording().catch((err: unknown) => {
      console.error('[ChatInterno] Erro ao iniciar gravação:', err)
      onCancel()
    })

    return () => {
      if (record.isRecording()) {
        record.stopRecording()
      }
      ws.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleStop = useCallback(() => {
    if (recordRef.current?.isRecording()) {
      recordRef.current.stopRecording()
    }
  }, [])

  const handlePlayPause = useCallback(() => {
    if (hasRecording && wavesurferRef.current) {
      wavesurferRef.current.playPause()
    }
  }, [hasRecording])

  return (
    <div className="flex items-center gap-2 w-full px-4 py-2">
      {/* Discard */}
      <button
        onClick={onCancel}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-(--color-danger) hover:bg-(--color-danger)/10 transition-colors"
        title="Descartar gravação"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
        </svg>
      </button>

      {/* Waveform */}
      <div ref={containerRef} className="flex-1 min-w-0" />

      {/* Duration */}
      <span className="text-xs font-mono text-(--color-text-muted) shrink-0 w-10 text-center">
        {duration}
      </span>

      {/* Stop or Play/Pause */}
      {isRecording ? (
        <button
          onClick={handleStop}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-(--color-danger)/10 text-(--color-danger) hover:bg-(--color-danger)/20 transition-colors"
          title="Parar gravação"
        >
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
        </button>
      ) : hasRecording ? (
        <button
          onClick={handlePlayPause}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-(--color-text-muted) hover:bg-(--color-bg-tertiary) hover:text-(--color-text-primary) transition-colors"
          title={isPlaying ? 'Pausar' : 'Reproduzir'}
        >
          {isPlaying ? (
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5.14v14l11-7-11-7z" />
            </svg>
          )}
        </button>
      ) : null}
    </div>
  )
}
